/**
 * 講義ステータス リアルタイム更新フック
 * 
 * Question Backend CDK の AppSync Events に接続し、
 * 講義ステータスの変更をリアルタイムで受信する。
 * 
 * useAppSyncConnection を使用してWebSocket接続を共有。
 */

import { useState, useEffect, useRef } from 'react';
import { useAppSyncConnection, type UseAppSyncConnectionOptions } from './useAppSyncConnection';

// =============================================================================
// 型定義
// =============================================================================

/** 講義ステータス */
export type LectureStatus = 'ready' | 'running' | 'ended';

/** 講義ステータス変更メッセージ */
export interface LectureStatusChangePayload {
  type: 'lecture-status-change';
  status: LectureStatus;
  tenant_id: string;
  lecture_id: string;
  timestamp: number;
  changed_by: string;
}

export interface StatusChangePayload extends LectureStatusChangePayload {}

export interface UseRealtimeLectureStatusOptions {
  tenantId: string;
  lectureId: string;
  enabled?: boolean;
  userId?: string; // ユーザーID（オプション）
  onStatusChange?: (status: LectureStatus, payload: LectureStatusChangePayload) => void;
  /** チャンネル購読成功時（初回・再接続とも）に呼ばれる。切断中のステータス変更取りこぼしを再同期する契機 */
  onSubscribeSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseRealtimeLectureStatusReturn {
  currentStatus: LectureStatus | null;
  isConnected: boolean;
  error: string | null;
}

// =============================================================================
// フック本体
// =============================================================================

export function useRealtimeLectureStatus(
  options: UseRealtimeLectureStatusOptions
): UseRealtimeLectureStatusReturn {
  const { tenantId, lectureId, enabled = true, userId, onStatusChange, onSubscribeSuccess, onError } = options;

  const [currentStatus, setCurrentStatus] = useState<LectureStatus | null>(null);
  const subscriptionIdRef = useRef<string | null>(null);

  const callbacksRef = useRef({
    onStatusChange,
    onSubscribeSuccess,
    onError,
  });
  callbacksRef.current = {
    onStatusChange,
    onSubscribeSuccess,
    onError,
  };

  // 共通接続フックを使用
  const connectionOptions: UseAppSyncConnectionOptions = {
    tenantId,
    lectureId,
    enabled,
    userId: userId || '', // 空文字列も明示的に渡す
  };
  if (onError) {
    connectionOptions.onError = onError;
  }
  const { isConnected, error, subscribe, unsubscribe } = useAppSyncConnection(connectionOptions);

  // ------------------------------------------------------------------
  // メッセージハンドラ
  // ------------------------------------------------------------------
  const handleStatusMessage = (event: unknown) => {
    console.log('[RealtimeLectureStatus] Received message:', event);
    
    // AppSyncの標準フォーマットをチェック（eventType + payload構造）
    if (
      typeof event === 'object' &&
      event !== null &&
      'eventType' in event &&
      event.eventType === 'LectureStatusChanged' &&
      'payload' in event
    ) {
      const payload = (event as { payload: LectureStatusChangePayload }).payload;
      console.log('[RealtimeLectureStatus] Status changed:', payload.status);

      setCurrentStatus(payload.status);
      callbacksRef.current.onStatusChange?.(payload.status, payload);
      return;
    }
    
    // 旧フォーマットのチェック（後方互換性）
    if (
      typeof event === 'object' &&
      event !== null &&
      'type' in event &&
      event.type === 'lecture-status-change'
    ) {
      const payload = event as LectureStatusChangePayload;
      console.log('[RealtimeLectureStatus] Status changed (legacy format):', payload.status);

      setCurrentStatus(payload.status);
      callbacksRef.current.onStatusChange?.(payload.status, payload);
      return;
    }
    
    console.warn('[RealtimeLectureStatus] Unknown message format:', event);
  };

  // ------------------------------------------------------------------
  // チャンネルサブスクリプション
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !tenantId || !lectureId) {
      return;
    }

    const channel = `question/${tenantId}/${lectureId}/lecture-status`;

    console.log('[RealtimeLectureStatus] Subscribing to channel:', channel);

    const subscriptionId = subscribe({
      channel,
      onMessage: handleStatusMessage,
      onSubscribeSuccess: () => {
        console.log('[RealtimeLectureStatus] Subscription successful');
        // 初回・再接続とも通知し、切断中のステータス変更取りこぼしを再同期させる
        callbacksRef.current.onSubscribeSuccess?.();
      },
      onSubscribeError: (error) => {
        console.error('[RealtimeLectureStatus] Subscription error:', error);
      },
    });

    subscriptionIdRef.current = subscriptionId;

    return () => {
      if (subscriptionIdRef.current) {
        console.log('[RealtimeLectureStatus] Unsubscribing from channel');
        unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [enabled, tenantId, lectureId, subscribe, unsubscribe]);

  return {
    currentStatus,
    isConnected,
    error,
  };
}
